import { runtimeConfig, type RuntimeConfig, type DomainType, type DomainConfig, type MultiDomainRuntimeConfig } from './runtime-config.generated';
import { CYPRESS_E2E_PRIME_KEY } from './cypress-e2e-prime';

function isCypressE2ePrimed(): boolean {
  if (typeof window === 'undefined') return false;
  return (window as unknown as Record<string, boolean | undefined>)[CYPRESS_E2E_PRIME_KEY] === true;
}

export const Environment = {
  Dev: 'dev',
  Auto: 'auto',
  Int: 'int',
  Pre: 'pre',
  Prod: 'prod'
} as const;

export type EnvironmentType = typeof Environment[keyof typeof Environment];

function getCurrentEnvironment(): EnvironmentType {
  // Use environment from runtime config (set at build time)
  if ('environment' in runtimeConfig && runtimeConfig.environment && Object.values(Environment).includes(runtimeConfig.environment as EnvironmentType)) {
    return runtimeConfig.environment as EnvironmentType;
  }
  
  // For multi-domain configs, extract environment from the structure
  if ('isMultiDomain' in runtimeConfig && runtimeConfig.isMultiDomain && runtimeConfig.environment) {
    return runtimeConfig.environment as EnvironmentType;
  }
  
  // Fallback to VITE_ENVIRONMENT
  const envFromProcess = import.meta.env.VITE_ENVIRONMENT;
  if (envFromProcess && Object.values(Environment).includes(envFromProcess as EnvironmentType)) {
    return envFromProcess as EnvironmentType;
  }
  
  // Final fallback based on NODE_ENV
  return import.meta.env.PROD ? Environment.Prod : Environment.Dev;
}

function getDomainType(): DomainType {
  
  const hostname = window.location.hostname;
  if (hostname.endsWith('mysympleloan.com')) {
    return 'msl';
  }
  return 'sym'; // Default to sym for symplelending.com or any other domain
}

function isMultiDomainConfig(config: RuntimeConfig): config is MultiDomainRuntimeConfig {
  return 'isMultiDomain' in config && config.isMultiDomain === true;
}

class ClientConfig {
  private static instance: ClientConfig;
  private config: RuntimeConfig;
  private environment: EnvironmentType;
  private domainType: DomainType;
  private activeConfig: DomainConfig;

  private constructor() {
    this.environment = getCurrentEnvironment();
    this.config = runtimeConfig;
    this.domainType = getDomainType();
    
    // Determine active configuration based on domain type
    if (isMultiDomainConfig(this.config)) {
      this.activeConfig = this.config.domains[this.domainType];
      
      // Log multi-domain configuration load in debug mode
      if (this.activeConfig.debug) {
        console.log('🌐 Multi-domain configuration loaded');
        console.log('Environment:', this.environment);
        console.log('Domain Type:', this.domainType);
        console.log('API Base URL:', this.activeConfig.apiBaseUrl);
      }
    } else {
      // Single domain configuration - cast to DomainConfig for compatibility
      this.activeConfig = this.config as DomainConfig;
      
      // Log single domain configuration load in debug mode
      if (this.activeConfig.debug) {
        console.log('🏠 Single domain configuration loaded for environment:', this.environment);
        console.log('API Base URL:', this.activeConfig.apiBaseUrl);
      }
    }
  }

  public static getInstance(): ClientConfig {
    if (!ClientConfig.instance) {
      ClientConfig.instance = new ClientConfig();
    }
    return ClientConfig.instance;
  }

  // Environment checks
  get isDevelopment(): boolean {
    return this.environment === 'dev';
  }

  get isProduction(): boolean {
    return this.environment === 'prod';
  }


  // Domain information
  get currentDomainType(): DomainType {
    return this.domainType;
  }

  get isMultiDomainEnvironment(): boolean {
    return isMultiDomainConfig(this.config);
  }

  // Feature flags
  get shouldMockServices(): boolean {
    // Local / CI override: SSM may set mockServices=false while Cypress still needs mocks + __e2e.
    const viteOverride = import.meta.env.VITE_MOCK_SERVICES;
    if (viteOverride === 'true') return true;
    if (viteOverride === 'false') return false;
    if (isCypressE2ePrimed()) return true;
    return this.activeConfig.mockServices;
  }

  get isDebugEnabled(): boolean {
    return this.activeConfig.debug;
  }

  // Analytics feature flags
  get isAnalyticsEnabled(): boolean {
    return this.activeConfig.analytics.enabled;
  }

  get isGoogleTagManagerEnabled(): boolean {
    return this.activeConfig.analytics.googleTagManager;
  }

  get isHubspotEnabled(): boolean {
    return this.activeConfig.analytics.hubspot;
  }

  get isVwoEnabled(): boolean {
    return this.activeConfig.analytics.vwo;
  }

  get isTrustpilotEnabled(): boolean {
    return this.activeConfig.analytics.trustpilot;
  }

  // Configuration getters - now using active config
  get apiBaseUrl(): string {
    return this.activeConfig.apiBaseUrl;
  }

  get offersUrl(): string {
    return this.activeConfig.offersUrl;
  }

  get apiKey(): string {
    return this.activeConfig.apiKey;
  }

  get hubspotUrl(): string {
    return this.activeConfig.hubspotUrl;
  }

  get hubspotPortalId(): string {
    return this.activeConfig.hubspotPortalId;
  }

  get hubspotForms(): any {
    return this.activeConfig.hubspotForms;
  }
  
  get trustpilotBusinessUnitId(): string {
    return this.activeConfig.trustpilotBusinessUnitId;
  }

  get trustpilotDomain(): string {
    return this.activeConfig.trustpilotDomain;
  }

  get gtmContainer() {
    return {
      url: this.activeConfig.gtmContainer?.url || '',
      iId: this.activeConfig.gtmContainer?.iId || '',
      cId: this.activeConfig.gtmContainer?.cId || '',
      containerId: this.activeConfig.gtmContainer?.containerId || ''
    };
  }

  get rumApplicationId(): string {
    return this.activeConfig.rumApplicationId;
  }

  get awsRum() {
    return {
      applicationId: this.activeConfig.rumApplicationId,
      identityPoolId: this.activeConfig.awsRum.identityPoolId,
      region: this.activeConfig.awsRum.region,
      enabled: this.activeConfig.awsRum.enabled
    };
  }

  // Debug methods
  logConfig(): void {
    if (this.isDebugEnabled) {
      console.log('🔧 Client Configuration Debug Info:');
      console.log('===================================');
      console.log('Current Environment:', this.environment);
      
      if (this.isMultiDomainEnvironment) {
        console.log('🌐 Multi-Domain Environment: YES');
        console.log('Current Domain Type:', this.currentDomainType);
      } else {
        console.log('🌐 Multi-Domain Environment: NO');
      }
      
      console.log('API Base URL:', this.apiBaseUrl);
      console.log('Mock Services:', this.shouldMockServices);
      console.log('AWS RUM Enabled:', this.awsRum.enabled);
      console.log('Trustpilot Domain:', this.trustpilotDomain);
      console.log('GTM Container ID:', this.gtmContainer?.containerId);
      console.log('Hostname:', typeof window !== 'undefined' ? window.location.hostname : 'N/A');
      // Note: Don't log sensitive data like API keys in production
    }
  }

  /**
   * Validate configuration completeness
   */
  validateConfig(): boolean {
    const requiredFields: (keyof DomainConfig)[] = [
      'apiKey',
      'hubspotUrl',
      'hubspotPortalId',
      'apiBaseUrl',
      'trustpilotBusinessUnitId',
      'trustpilotDomain',
      'gtmContainerId',
      'rumApplicationId'
    ];

    // Check all required fields in the active configuration
    for (const field of requiredFields) {
      if (!this.activeConfig[field]) {
        const domainInfo = this.isMultiDomainEnvironment ? ` for domain: ${this.currentDomainType}` : '';
        console.error(`❌ Missing required configuration: ${String(field)}${domainInfo}`);
        return false;
      }
    }

    if (this.isDebugEnabled) {
      const domainInfo = this.isMultiDomainEnvironment ? ` for domain: ${this.currentDomainType}` : '';
      console.log(`✅ Configuration validation passed${domainInfo}`);
    }

    return true;
  }

  /**
   * Get current environment name
   */
  get environmentName(): string {
    return this.environment;
  }

  /**
   * Get environment display name
   */
  get environmentDisplayName(): string {
    const displayNames: Record<EnvironmentType, string> = {
      dev: 'Development',
      auto: 'Auto',
      int: 'Integration',
      pre: 'Pre-production',
      prod: 'Production'
    };

    return displayNames[this.environment] || 'Unknown';
  }
}

// Export singleton instance
export const clientConfig = ClientConfig.getInstance();

// Validate configuration on load
if (clientConfig.isDebugEnabled) {
  clientConfig.validateConfig();
}

// Export types for external use  
export type { RuntimeConfig };